import { useT } from "../i18n";
import type { Slot } from "../api";

function timeLabel(iso: string, _lang: string, tz: string): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: tz,
  }).format(d);
}

function hourInTz(iso: string, tz: string): number {
  const parts = new Intl.DateTimeFormat("en-US", { hour: "numeric", hour12: false, timeZone: tz }).formatToParts(
    new Date(iso),
  );
  return Number(parts.find((p) => p.type === "hour")?.value ?? 0);
}

type Period = "morning" | "afternoon" | "evening";

function periodOf(hour: number): Period {
  if (hour < 12) return "morning";
  if (hour < 18) return "afternoon";
  return "evening";
}

const PERIOD_ORDER: Period[] = ["morning", "afternoon", "evening"];
const PERIOD_LABEL_KEY: Record<Period, "periodMorning" | "periodAfternoon" | "periodEvening"> = {
  morning: "periodMorning",
  afternoon: "periodAfternoon",
  evening: "periodEvening",
};

export function SlotPicker({
  slots,
  loading,
  timezone,
  onPick,
}: {
  slots: Slot[];
  loading: boolean;
  timezone: string;
  onPick: (s: Slot) => void;
}) {
  const { t, lang } = useT();
  if (loading) return <div className="empty">{t("loadingSlots")}</div>;
  if (slots.length === 0) {
    return (
      <div className="empty" style={{ padding: "20px 16px" }}>
        <p style={{ marginBottom: 10 }}>{t("noSlots")}</p>
        <a
          href="https://wa.me/5521988407505?text=Hello%20Lucas!%20I%20am%20looking%20for%20an%20open%20architecture%20session%20slot%20and%20would%20like%20direct%20scheduling."
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: "12.5px",
            color: "var(--lime)",
            fontWeight: 600,
          }}
        >
          Request custom slot on WhatsApp (+55 21 98840-7505) ↗
        </a>
      </div>
    );
  }

  const grouped: Record<Period, Slot[]> = { morning: [], afternoon: [], evening: [] };
  for (const s of slots) grouped[periodOf(hourInTz(s.start, timezone))].push(s);

  return (
    <div className="slot-groups">
      {PERIOD_ORDER.filter((p) => grouped[p].length > 0).map((period) => (
        <div key={period} className="slot-group">
          <div className="slot-group-label">{t(PERIOD_LABEL_KEY[period])}</div>
          <div className="slots">
            {grouped[period].map((s) => (
              <button key={s.start} className="slot" type="button" onClick={() => onPick(s)}>
                {timeLabel(s.start, lang, timezone)}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
